/**
 * Ô tìm kiếm dùng chung — có nút xoá (OQ-230).
 *
 * Sáu màn đang lặp lại `<label className="search"><Icon/><input/></label>`, và
 * **không màn nào có cách xoá nhanh**: người dùng phải bôi đen rồi xoá, hoặc
 * nhấn Backspace từng ký tự. Với một bộ lọc mà người ta gõ rồi xoá liên tục,
 * đó là ma sát lặp lại hàng chục lần mỗi phiên.
 *
 * Gom vào một component thay vì sửa sáu chỗ: nút xoá là thứ dễ quên ở màn thứ
 * bảy, và một khác biệt nhỏ giữa các màn (kích thước, `aria-label`, phím tắt)
 * là thứ người dùng phải học lại mỗi lần đổi màn.
 */

import { useRef, type ReactNode } from 'react';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';

export function SearchBox({
  value,
  onChange,
  placeholder,
  width,
  odId,
  children,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  width?: number | string;
  odId?: string;
  /** Nội dung phụ bên phải ô — ví dụ phím tắt `/` của thanh tìm kiếm toàn cục. */
  children?: ReactNode;
}) {
  const t = useT();
  const oRef = useRef<HTMLInputElement>(null);

  return (
    <label
      className="search"
      {...(width === undefined ? {} : { style: { width } })}
      {...(odId ? { 'data-od-id': odId } : {})}
    >
      <Icon name="search" size={16} />
      <input
        ref={oRef}
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          /*
           * Esc xoá ô — thói quen sẵn có từ thanh tìm kiếm của trình duyệt.
           * `stopPropagation` để Esc không đóng luôn hộp thoại đang chứa ô này:
           * người dùng muốn xoá chữ, không muốn mất cả màn hình đang làm dở.
           */
          if (event.key === 'Escape' && value) {
            event.stopPropagation();
            event.preventDefault();
            onChange('');
          }
        }}
      />
      {value ? (
        <button
          type="button"
          className="search-clear"
          aria-label={t('common.clearSearch')}
          title={t('common.clearSearch')}
          data-od-id="search-clear"
          onClick={() => {
            onChange('');
            /* Trả con trỏ về ô: người ta xoá là để gõ lại, không phải để dừng. */
            oRef.current?.focus();
          }}
        >
          <Icon name="close" size={14} />
        </button>
      ) : null}
      {children}
    </label>
  );
}
